import type {
  ConnectionStatus,
  WebSocketMessage,
  MessageCallback,
  StatusChangeCallback,
  ErrorCallback
} from './types'
import { WebSocketConnection } from './WebSocketConnection'

/**
 * Interface for channel subscription management
 */
export interface IChannelSubscription {
  subscribe(): Promise<void>
  unsubscribe(): void
  onMessage(callback: MessageCallback): () => void
  onStatusChange(callback: StatusChangeCallback): () => void
  onError(callback: ErrorCallback): () => void
  getStatus(): ConnectionStatus
  isSubscribed(): boolean
  getConnection(): WebSocketConnection
}

/**
 * Channel subscription implementation that wraps WebSocketConnection
 * with subscription-specific logic and message routing
 */
export class ChannelSubscription implements IChannelSubscription {
  private connection: WebSocketConnection
  private channelName: string
  private isActive: boolean = false
  private messageUnsubscribers: (() => void)[] = []
  private statusUnsubscribers: (() => void)[] = []
  private errorUnsubscribers: (() => void)[] = []

  constructor(channelName: string, connection: WebSocketConnection) {
    this.channelName = channelName
    this.connection = connection
  }

  /**
   * Subscribe to the channel
   */
  async subscribe(): Promise<void> {
    if (this.isActive) {
      return
    }

    this.isActive = true

    try {
      await this.connection.connect()

      // Send subscription message to backend
      this.connection.send({
        type: 'subscribe',
        channel: this.channelName
      })
    } catch (error) {
      this.isActive = false
      throw error
    }
  }

  /**
   * Unsubscribe from the channel
   */
  unsubscribe(): void {
    if (!this.isActive) {
      return
    }

    this.isActive = false

    // Send unsubscribe message to backend if connected
    if (this.connection.isConnected()) {
      this.connection.send({
        type: 'unsubscribe',
        channel: this.channelName
      })
    }

    // Clean up all callback subscriptions
    this.messageUnsubscribers.forEach((unsub) => unsub())
    this.statusUnsubscribers.forEach((unsub) => unsub())
    this.errorUnsubscribers.forEach((unsub) => unsub())

    this.messageUnsubscribers.length = 0
    this.statusUnsubscribers.length = 0
    this.errorUnsubscribers.length = 0
  }

  /**
   * Add message callback with channel-specific filtering
   */
  onMessage(callback: MessageCallback): () => void {
    const filteredCallback: MessageCallback = (message: WebSocketMessage) => {
      // Only process messages for this specific channel
      if (this.isMessageForChannel(message)) {
        callback(message)
      }
    }

    const unsubscriber = this.connection.onMessage(filteredCallback)
    this.messageUnsubscribers.push(unsubscriber)

    return () => {
      unsubscriber()
      const index = this.messageUnsubscribers.indexOf(unsubscriber)
      if (index > -1) {
        this.messageUnsubscribers.splice(index, 1)
      }
    }
  }

  /**
   * Add status change callback
   */
  onStatusChange(callback: StatusChangeCallback): () => void {
    const unsubscriber = this.connection.onStatusChange(callback)
    this.statusUnsubscribers.push(unsubscriber)

    return () => {
      unsubscriber()
      const index = this.statusUnsubscribers.indexOf(unsubscriber)
      if (index > -1) {
        this.statusUnsubscribers.splice(index, 1)
      }
    }
  }

  /**
   * Add error callback
   */
  onError(callback: ErrorCallback): () => void {
    const unsubscriber = this.connection.onError(callback)
    this.errorUnsubscribers.push(unsubscriber)

    return () => {
      unsubscriber()
      const index = this.errorUnsubscribers.indexOf(unsubscriber)
      if (index > -1) {
        this.errorUnsubscribers.splice(index, 1)
      }
    }
  }

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return this.connection.getStatus()
  }

  /**
   * Check if subscription is active
   */
  isSubscribed(): boolean {
    return this.isActive
  }

  /**
   * Get channel name
   */
  getChannelName(): string {
    return this.channelName
  }

  /**
   * Get underlying connection
   */
  getConnection(): WebSocketConnection {
    return this.connection
  }

  /**
   * Check if a message is intended for this channel
   */
  private isMessageForChannel(message: WebSocketMessage): boolean {
    // For case channels, check if the case_uuid matches the channel name
    if (this.channelName.startsWith('case_')) {
      const caseUuid = this.channelName.replace('case_', '')
      return message.data?.case_uuid === caseUuid
    }

    // For other channel types, we could add more specific logic here
    // For now, assume all messages are relevant if no specific filtering is needed
    return true
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.unsubscribe()
  }
}
